import { calculatePricing } from '@/lib/pricing';

/**
 * Get the minimum REAL price (what the runner actually pays) from event races.
 * Takes fees_included into account to calculate the actual price.
 * Priority: races with non-zero prices > display_price_cents > price_cents
 */
export function getMinRacePrice(event: { 
  price_cents: number; 
  display_price_cents?: number | null;
  commission_rate?: number | null;
  races?: Array<{ price_cents: number; price_cents_ffa?: number | null; fees_included?: boolean | null }>;
}): number {
  // Priority 1: Minimum real price from races if available
  if (event.races && event.races.length > 0) {
    const realPrices = event.races
      .filter((r) => r.price_cents > 0 || (r.price_cents_ffa != null && r.price_cents_ffa > 0))
      .flatMap((r) => {
        const prices: number[] = [];
        // Standard price
        if (r.price_cents > 0) {
          const pricing = calculatePricing(
            r.price_cents, 
            r.fees_included ?? true,
            event.commission_rate
          );
          prices.push(pricing.runnerPays);
        }
        // FFA price (usually lower)
        if (r.price_cents_ffa != null && r.price_cents_ffa > 0) {
          const pricingFfa = calculatePricing(
            r.price_cents_ffa, 
            r.fees_included ?? true,
            event.commission_rate
          );
          prices.push(pricingFfa.runnerPays);
        }
        return prices;
      });
    
    if (realPrices.length > 0) {
      return Math.min(...realPrices);
    }
  }
  
  // Priority 2: display_price_cents if it exists and is > 0
  if (event.display_price_cents != null && event.display_price_cents > 0) {
    return event.display_price_cents;
  }
  
  // Fallback: price_cents (can be 0 = Free)
  return event.price_cents || 0;
}

/**
 * Format a price in cents to a display string.
 */
export function formatPrice(priceCents: number, currency: string = 'EUR'): string {
  if (priceCents === 0) {
    return 'Gratuit';
  }
  return (priceCents / 100).toLocaleString('fr-FR', { 
    style: 'currency', 
    currency 
  });
}

/**
 * Format a price in cents to a short display string (e.g., "14€").
 */
export function formatPriceShort(priceCents: number): string {
  if (priceCents === 0) {
    return 'Gratuit';
  }
  return `${(priceCents / 100).toFixed(0)}€`;
}
